import { PrismicRichText } from '@prismicio/react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { whenProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import {
  IconWrapper as TriangleIconWrapper,
  ColumnTitleDescription,
} from './BenefitsPlanTableSlice';
import {
  Description,
  Title,
  Table,
  ColumnTitle,
  IconWrapper,
  Column,
  IconContainer,
  CloseIconWrapper,
  MobileColumnTitle,
  ColumnItem,
  TitleRow,
  TableRow,
  Note,
  MobileTable,
  MobileTableRow,
  Section,
  icons,
} from './TableSlice';
import { normaIcons } from './benefit-icons';
import TriangleIcon from './assets/triangle.svg';

import { fieldHasContent, transformProperties } from '@/src/domains/prismic/helpers';
import { fromTabletBreakpoint, tabletBreakpoint } from '@/src/domains/shared/breakpoints';

export const FlexibleColumnStyle = css`
  width: ${({ columnsAmount }) => 100 / columnsAmount}%;

  ${({ columnsAmount }) => {
    switch (columnsAmount) {
      case 2:
        return css`
          &:nth-child(4) {
            display: none;
          }

          &:nth-child(3) {
            display: none;
          }

          &:nth-child(2) {
            ${fromTabletBreakpoint(css`
              border-right: none;
            `)};
          }
        `;
      case 3:
        return css`
          &:nth-child(4) {
            display: none;
          }

          &:nth-child(3) {
            ${fromTabletBreakpoint(css`
              border-right: none;
            `)};
          }
        `;
      default:
        return css`
          &:nth-child(4) {
            ${fromTabletBreakpoint(css`
              border-right: none;
            `)};
          }
        `;
    }
  }}
`;

const FlexibleTitleRow = styled(TitleRow)`
  ${whenProp(
    '$purpleTone',
    css`
      && {
        background-color: rgba(20, 25, 30, 0.1);
        border-top-left-radius: 50px;
        border-top-right-radius: 50px;
      }
    `
  )}
`;

const FlexibleColumn = styled(Column)`
  && {
    && {
      position: relative;

      &:first-child {
        padding-left: 20px;
      }

      ${FlexibleColumnStyle}

      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      padding: 15px;
      border-right: 1px solid var(--colors-irisBlue);

      h3 {
        margin-bottom: 0;
      }
    }
  }
`;

const FlexibleColumnItem = styled(ColumnItem)`
  && {
    &:nth-child(2) {
      border-left: none;
    }

    ${fromTabletBreakpoint(css`
      &:not(:last-child) {
        border-right: 1px solid var(--colors-irisBlue);
      }

      &:first-child {
        padding-left: 20px;
      }
    `)};

    ${tabletBreakpoint(css`
      width: 100%;
      padding: 0;

      p,
      ul,
      ol {
        padding: 0 20px;
      }

      &:nth-child(2) {
        border-left: 1px solid var(--colors-irisBlue);
      }

      ${whenProp(
        '$purpleTone',
        css`
          ${Column} {
            background-color: rgba(20, 25, 30, 0.1);
          }
        `
      )}

      ${Column} {
        border-bottom: 1px solid var(--colors-irisBlue);
        width: 100%;
        margin: 0;
        padding: 20px 10px;
      }

      ${MobileColumnTitle} {
        padding-bottom: 0;
        flex-direction: column;
        align-items: center;
      }

      ${IconWrapper} {
        padding-bottom: 15px;
      }

      ${TriangleIconWrapper} {
        display: none;
      }
    `)}
    ${FlexibleColumnStyle}
  }
`;

const CloseIcon = styled(normaIcons['times-full'])`
  width: 42px;
  height: 42px;
  color: var(--newBrandColor);
  opacity: 0.6;
`;

const MobileIconContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 25px 0;
  border-top: 1px solid var(--colors-irisBlue);
  background: #fafafa;
`;

export function ColumnTitleContent({ title, icon, description, purpleTableHeader }) {
  return (
    <>
      {icon !== 'no icon' && <IconWrapper>{icons[icon] || null}</IconWrapper>}
      {fieldHasContent(title) && (
        <ColumnTitle>
          <PrismicRichText field={title} />
        </ColumnTitle>
      )}
      {fieldHasContent(description) && (
        <ColumnTitleDescription>
          <PrismicRichText field={description} />
        </ColumnTitleDescription>
      )}
      {purpleTableHeader && (
        <TriangleIconWrapper>
          <TriangleIcon />
        </TriangleIconWrapper>
      )}
    </>
  );
}

export function TwoFourColumnsTable({ slice }) {
  const { primary, fields: table } = transformProperties(slice);

  const {
    sectionId,
    columns,
    title,
    description,
    countryDescription,
    purpleTableHeader,
    firstIcon,
    firstColumnTitle,
    secondIcon,
    secondColumnTitle,
    thirdIcon,
    thirdColumnTitle,
    fourthIcon,
    fourthColumnTitle,
    note,
    firstReducedOpacity,
    secondReducedOpacity,
    thirdReducedOpacity,
    fourthReducedOpacity,
    firstColumnPriceDescription,
    secondColumnPriceDescription,
    thirdColumnPriceDescription,
    fourthColumnPriceDescription,
  } = primary;
  const columnsAmount = columns || 2;

  const columnsData = [
    {
      opacity: firstReducedOpacity,
      icon: firstIcon,
      title: firstColumnTitle,
      description: firstColumnPriceDescription,
    },
    {
      opacity: secondReducedOpacity,
      icon: secondIcon,
      title: secondColumnTitle,
      description: secondColumnPriceDescription,
    },
    {
      opacity: thirdReducedOpacity,
      icon: thirdIcon,
      title: thirdColumnTitle,
      description: thirdColumnPriceDescription,
    },
    {
      opacity: fourthReducedOpacity,
      icon: fourthIcon,
      title: fourthColumnTitle,
      description: fourthColumnPriceDescription,
    },
  ];

  return (
    <Section id={sectionId}>
      <ContentContainer>
        <div>
          {fieldHasContent(title) && (
            <Title>
              <PrismicRichText field={title} />
            </Title>
          )}
          {fieldHasContent(countryDescription) && (
            <Description>
              <PrismicRichText field={countryDescription} />
            </Description>
          )}
          {fieldHasContent(description) && (
            <Description>
              <PrismicRichText field={description} />
            </Description>
          )}
        </div>
        <Table>
          <FlexibleTitleRow $purpleTone={purpleTableHeader} columnsAmount={columnsAmount}>
            {columnsData.map(
              ({ opacity, icon, title: columnTitle, description: columnDescription }, index) => (
                <FlexibleColumn columnsAmount={columnsAmount} key={index} $opacity={opacity}>
                  <ColumnTitleContent
                    icon={icon}
                    title={columnTitle}
                    description={columnDescription}
                    purpleTableHeader={purpleTableHeader}
                  />
                </FlexibleColumn>
              )
            )}
          </FlexibleTitleRow>
          <div>
            {table.map(({ firstColumn, secondColumn, thirdColumn, fourthColumn }, index) => {
              const columnsContent = [firstColumn, secondColumn, thirdColumn, fourthColumn];

              return (
                <TableRow key={index}>
                  {columnsContent.map((columnData, i) => (
                    <FlexibleColumnItem
                      columnsAmount={columnsAmount}
                      key={i}
                      $opacity={columnsData[i]}
                      $empty={!fieldHasContent(columnsContent[i])}
                    >
                      <PrismicRichText field={columnData} />
                      <IconContainer>
                        <CloseIconWrapper>
                          <CloseIcon />
                        </CloseIconWrapper>
                      </IconContainer>
                    </FlexibleColumnItem>
                  ))}
                </TableRow>
              );
            })}
          </div>
        </Table>

        <MobileTable>
          {table.map(({ firstColumn, secondColumn, thirdColumn, fourthColumn }, index) => {
            const columnsContent = [firstColumn, secondColumn, thirdColumn, fourthColumn];

            return (
              <MobileTableRow key={index}>
                {columnsContent.map((columnData, i) => (
                  <FlexibleColumnItem
                    columnsAmount={columnsAmount}
                    key={i}
                    $opacity={columnsData[i]?.opacity}
                    $purpleTone={purpleTableHeader}
                  >
                    <Column>
                      <MobileColumnTitle>
                        <ColumnTitleContent
                          icon={columnsData[i]?.icon}
                          title={columnsData[i]?.title}
                          description={columnsData[i]?.description}
                        />
                      </MobileColumnTitle>
                    </Column>
                    <PrismicRichText field={columnData} />
                    {!fieldHasContent(columnsContent[i]) && (
                      <MobileIconContainer>
                        <CloseIcon />
                      </MobileIconContainer>
                    )}
                  </FlexibleColumnItem>
                ))}
              </MobileTableRow>
            );
          })}
        </MobileTable>
        {fieldHasContent(note) && (
          <Note>
            <PrismicRichText field={note} />
          </Note>
        )}
      </ContentContainer>
    </Section>
  );
}
